import React, { useCallback, useEffect } from 'react';
import DashboardCard from 'components/DashboardCard/DashboardCard';
import { Add, Assignment } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from '@tanstack/react-router';
import { DataGrid } from '@mui/x-data-grid';
import { actions } from '../PatientView.redux';
import { Button } from '@mui/material';
import PatientChartsCell from 'pages/PatientView/PatientCharts/PatientChartsCell';
import NumberedDataGridPagination from 'components/NumberedDataGridPagination';

const PatientCharts = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { patient_id } = useParams({ from: '/patients/view/$patient_id' });
    const hasLoaded = useSelector((state) => state.patientView.chartsPreviouslyLoaded);
    const loading = useSelector((state) => state.patientView.chartsLoading);
    const charts = useSelector((state) => state.patientView.charts);
    const loadCharts = (patientID) => dispatch(actions.loadCharts(patientID));

    const handleAddNew = useCallback(() => {
        dispatch(actions.addNewChart(patient_id, undefined, navigate));
    }, [dispatch, patient_id, navigate]);

    const columns = [
        { field: 'date_created', headerName: 'Date', flex: 1, minWidth: 140, sortable: false },
        { field: 'patient_name', headerName: 'Name', flex: 1, minWidth: 150, sortable: false },
        { field: 'patient_weight', headerName: 'Weight', flex: 1, minWidth: 120, sortable: false },
        { field: 'is_neutered', headerName: 'Neutered', flex: 1, minWidth: 120, sortable: false },
        { field: 'created_by', headerName: 'Created by', flex: 1, minWidth: 150, sortable: false },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 120,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            renderCell: (params) => <PatientChartsCell {...params} />,
        },
    ];

    useEffect(() => {
        if (!hasLoaded && !loading) {
            loadCharts(patient_id);
        }
    }, [loadCharts, patient_id, hasLoaded, loading]);

    return (
        <DashboardCard
            color={'deepPurple'}
            icon={<Assignment />}
            topContent={'Charts'}
            topLabel={
                <div>
                    <div>Click a chart to edit, or</div>
                    <div>
                        <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            startIcon={<Add />}
                            onClick={handleAddNew}
                            disabled={loading}
                        >
                            Add New
                        </Button>
                    </div>
                </div>
            }
            size="xl"
        >
            <div style={{ width: '100%' }}>
                <DataGrid
                    rows={charts}
                    columns={columns}
                    getRowId={(row) => row.visit_id}
                    pagination
                    pageSizeOptions={[5, 10, 20, 50]}
                    initialState={{
                        pagination: {
                            paginationModel: { page: 0, pageSize: 10 },
                        },
                    }}
                    loading={loading}
                    disableRowSelectionOnClick
                    disableColumnMenu
                    disableCellFocus
                    sx={{
                        '& .MuiDataGrid-row': { cursor: 'pointer' },
                        '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': { outline: 'none' },
                        '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
                            outline: 'none',
                        },
                    }}
                    slots={{ pagination: NumberedDataGridPagination }}
                    onRowClick={(params) => {
                        if (params.field !== 'actions') {
                            navigate({ to: `/chart/${params.row.visit_id}` });
                        }
                    }}
                    autoHeight
                />
            </div>
        </DashboardCard>
    );
};

export default PatientCharts;
